/**
 * 화면 스타일(크롬) 팩 — 라이트/다크·브랜드색과 별개 축
 * type1: 현재 밀집 ERP (디폴트)
 * type2: 클로브AI 영감(복제 아님) — 여백·카드·라운드·큰 컨트롤
 */

export const UI_CHROME_TYPE_IDS = ['type1', 'type2'];

/** @param {unknown} v */
export function normalizeUiChromeType(v) {
  return v === 'type2' ? 'type2' : 'type1';
}

export const UI_CHROME_CATALOG = [
  {
    id: 'type1',
    label: '업무 밀집',
    badge: '기본',
    summary: '지금 ERP와 동일. 좁은 사이드바, 밀집 그리드, 작은 버튼.',
  },
  {
    id: 'type2',
    label: '모던 워크벤치',
    badge: '클로브AI 영감',
    summary: '넓은 여백, 흰 카드, 둥근 메뉴·버튼. 브랜드색은 그대로 둡니다.',
  },
];

/**
 * 레이아웃 토큰만. 색(primary)은 appearance 슬롯이 담당.
 * type2 서피스는 라이트 모드에서만 옅은 캔버스(클로브류 SaaS).
 */
export const UI_CHROME_PACKS = {
  type1: {
    layout: {
      sidebarWidth: '240px',
      sidebarCollapsedWidth: '56px',
      pagePadding: '12px',
      contentGap: '8px',
    },
    borderRadius: {
      xs: '2px',
      sm: '4px',
      md: '8px',
      lg: '12px',
      pill: '999px',
      panel: '8px',
    },
    components: {
      actionButton: {
        paddingY: '3px',
        paddingX: '8px',
        minHeight: '26px',
        fontSizeKey: 'sm',
        gap: '0.25rem',
      },
      form: {
        searchLabelWidth: '56px',
        fieldGap: '6px',
        rowGap: '12px',
        controlHeight: '28px',
      },
      searchPanel: {
        barMinHeight: '28px',
        barPadding: '2px 6px 2px 10px',
      },
      sidebar: {
        itemHeight: '32px',
        itemRadius: '8px',
      },
      page: {
        panelShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        toolbarShadow: 'none',
      },
    },
    surface: null,
  },
  type2: {
    layout: {
      sidebarWidth: '268px',
      sidebarCollapsedWidth: '64px',
      pagePadding: '20px',
      contentGap: '16px',
    },
    borderRadius: {
      xs: '6px',
      sm: '10px',
      md: '14px',
      lg: '20px',
      pill: '999px',
      panel: '16px',
    },
    components: {
      actionButton: {
        paddingY: '8px',
        paddingX: '14px',
        minHeight: '36px',
        fontSizeKey: 'sm',
        gap: '0.4rem',
      },
      form: {
        searchLabelWidth: '72px',
        fieldGap: '8px',
        rowGap: '16px',
        controlHeight: '36px',
      },
      searchPanel: {
        barMinHeight: '40px',
        barPadding: '8px 12px',
      },
      sidebar: {
        itemHeight: '40px',
        itemRadius: '12px',
      },
      page: {
        panelShadow: '0 1px 2px rgba(15, 23, 42, 0.05), 0 8px 24px rgba(15, 23, 42, 0.04)',
        toolbarShadow: '0 1px 2px rgba(15, 23, 42, 0.05), 0 8px 24px rgba(15, 23, 42, 0.04)',
      },
    },
    surface: {
      pageBg: '#F3F5F7',
      sidebarBg: '#FFFFFF',
    },
  },
};

/**
 * theme 객체에 크롬 팩을 덮어쓴다 (색 슬롯 적용 이후).
 * @param {import('./theme').theme} next
 * @param {string} chromeType
 */
export function applyUiChromeToTheme(next, chromeType) {
  const id = normalizeUiChromeType(chromeType);
  const pack = UI_CHROME_PACKS[id] || UI_CHROME_PACKS.type1;

  next.layout = { ...next.layout, ...pack.layout };
  next.borderRadius = { ...next.borderRadius, ...pack.borderRadius };
  next.components = {
    ...next.components,
    actionButton: { ...next.components.actionButton, ...pack.components.actionButton },
    form: { ...next.components.form, ...pack.components.form },
    searchPanel: pack.components.searchPanel,
    sidebar: pack.components.sidebar,
    page: pack.components.page,
  };
  next.shadows = {
    ...next.shadows,
    panel: pack.components.page.panelShadow,
    toolbar: pack.components.page.toolbarShadow,
  };

  const mode = next.meta?.colorMode || 'light';
  if (pack.surface && mode !== 'dark') {
    next.colors.background.page = pack.surface.pageBg;
    next.colors.background.sidebar = pack.surface.sidebarBg;
  }

  next.meta = { ...(next.meta || {}), uiChromeType: id };
  return next;
}
